import React, { FC, FormEvent, HTMLAttributes, ReactNode, RefObject, createElement, useCallback } from 'react';
import { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
import mergeInPlace from 'lodash/merge';
import setInPlace from 'lodash/set';
import cloneDeep from 'lodash/fp/cloneDeep';
import memoize from 'memoize-one';

import { SCP } from 'ia-react-core/~/interfaces/SCP';
import { DeepPartial } from 'ia-react-core/~/interfaces/DeepPartial';
import submitFormById from 'ia-react-core/~/actions/form-fn/submitFormById';
import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import { NestedFormInfo } from 'ia-react-core/~/interfaces/NestedFormInfo';
import { FormState } from 'ia-react-core/~/interfaces/FormState';
import { ControlInfo } from 'ia-react-core/~/interfaces/ControlInfo';

import { ControlState } from 'ia-react-core/~/interfaces/ControlState';
import updateFormById from 'ia-react-core/~/actions/form-fn/updateFormById';
import { ErrorMap } from 'ia-react-core/~/interfaces/ErrorMap';
import { WithInternapsValidatorsProps, WithValidatorsProps } from 'ia-react-core/~/hocs/form/#withValidators';
import throwError from 'ia-react-core/~/utils/error/#throwError';
import Schema from 'ia-react-core/~/schemas/Schema';
import scrollIntoView from '../../../utils/dom/scrollIntoView';
import coreLogger from '../../../utils/log/coreLogger';
import get from '../../../utils/accessors/get';
import set from '../../../utils/accessors/set';
import merge from '../../../utils/accessors/merge';
import mergeState from '../../../utils/accessors/#mergeState';
import getScoped from '../../../utils/redux/getScoped';
import registerVirtuals from '../../../utils/redux/#registerVirtuals';
import filterKnownProps from '../../../utils/components/#filterKnownProps';
import FORM from '../../../constants/FORM';
import FormContext from '../FormContext';
import FormColContext from '../#FormColContext';
import formActions from './Form.actions';
import formSelectors from './Form.selectors';
import ControlGrouper from '../#ControlGrouper';
import FormMessages from '../#FormMessages';
import { FormRegistration } from './withFormMethods';

export interface FormProps<Values> extends Omit<SCP<'form'>, 'autoComplete' | 'onSubmit' | 'children'>, WithValidatorsProps<Values, {}> {
  children: ReactNode | ((renderProps: {
    formId: number;
    name: string;
    valuesDst: string;
  }) => ReactNode);
  /** The name of the form, used as the reducer key. If not provided, the reducer key will be "Form${formId}". */
  name?: string;
  /** The path on the redux store where to read the values from, based on the current scope. */
  valuesSrc?: string;
  /** The path on the redux store where to write the values to, based on the current scope. */
  valuesDst?: string;
  /** Values to initialize the form with. */
  initialValues?: DeepPartial<Values>;
  /** Defaults values for the forms. It is usually preferable to define default directly on controls to be more modular. The difference between `initialValues` and `defaultValues` is that when the form is cleared, each control is set to its defaultValue, while any initialValue is cleared. */
  defaultValues?: DeepPartial<Values>;
  /** Callback called with the latest form values when the form submits and has passed validations. Setting `onSubmit` triggers the [Auto complete](/Core/Components/Form/Form?id=submission) mode */
  onSubmit?: (formValues: Values) => void | boolean | Error | Promise<void | boolean | Error>;
  /** Sets the default number of columns each `FormCol` spreads. */
  defaultCol?: number;
  /** Sets the minimum width of each `FormCol` in pixels. Default of 200px. */
  minColWidth?: number;
  /** Determines if the form should complete its submission automatically, or if it should stay in the "isSubmitting" state until told by the completeSubmitFormById and completeSubmitFormByName. */
  autoComplete?: boolean;
  /** When use on a subForm, indicates that form should be isolated from its parent and act independently */
  isolate?: boolean;
  /** Disables every controls of the form. */
  disabled?: boolean;
  /** Set every controls of the form to `readOnly`. */
  readOnly?: boolean;
  /** Set every controls of the form to `textOnly`. */
  textOnly?: boolean;
  /** Scrolls to first error when the form is submitted. */
  scrollToFirstError?: boolean;
  /** Clean the values from the state when unmounting the form. */
  cleanValues?: boolean;
  /** Override the tag use for the form. */
  as?: string;
  /** Hoist the control errors to the top of the form instead of being displayed next to each control. */
  hoistErrors?: boolean;
  /** Title of the error messages when display at the top with hoistErrors. */
  errorMessageTitle?: string;
  /** Title of the warning messages when display at the top with hoistErrors. */
  warningMessageTitle?: string;
  /** Title of the caution messages when display at the top with hoistErrors. */
  cautionMessageTitle?: string;
  /** Hide the error messages under the controls. Can be overriden on each control. "true" by default when using "hoistErrors"; set to "false" to preserve the messages under the controls.  */
  hideErrorMessages?: boolean | string[];
  /** Allows invalid valid to being saved on the redux Store and allows an invalid form to be submitted. Individual control can be overriden by passing "allowInvalid" directly on them. */
  allowInvalid?: boolean;
  /** Hide any hoisted message anchor for this control. Can be overriden on each control. */
  hideHoistedAnchor?: boolean;
  /** @ignore */
  formId?: number;
}

interface FormInternalProps<Values> extends Omit<NestedFormInfo<Values>, 'name' | 'validate' | 'reset'>, WithInternapsValidatorsProps<Values, {}> {
  formId: number;
  ancestorFormId: number;
  parentFormScope: string[];
  debounceFormStateDelay?: number;
  formReducerKey: string;
  ancestorFormReducerKey: string;
  store: AugmentedStore<any>;
  scope: string[];
  isolatedSubForm: boolean;
  dispatchSubmitFormById: typeof submitFormById;
  ancestorValuesDst: string;
  nestedControlInfos: ControlInfo[];
  nestedFormInfos: NestedFormInfo<Values>[];
  parentFormReducerKey: string;
  originalValuesDst: string;
  ancestorFormReducerScope: string[];
  valuesToStateFormatters?: ((values: unknown, oldValues: unknown) => DeepPartial<Values>)[];
  parentFormId: number;
  schema: Schema;
  latestInitialValues?: DeepPartial<Values>;
  registrations: FormRegistration<Values>[];
  unregisterForm: (formInfo: { formId: number; name: string }) => void;
  registerForm: (formInfo: NestedFormInfo<object>) => void;
  validateForm: (values: object) => ErrorMap;
  touchForm: () => void;
  resetForm: (initialValues: DeepPartial<Values>, newFormState: DeepPartial<FormState>, options?: { resetValues?: boolean; resetState?: boolean }) => { values: Values; formState: DeepPartial<FormState> };
  dispatchUpdateFormById: typeof updateFormById;
  ancestorCommitFormState: typeof Form.prototype.scheduleCommitFormState;
  ancestorCommitFormValues: typeof Form.prototype.scheduleCommitFormValues;
  watchRegistrations: (callback: FormRegistration<Values>) => void;
}

export interface FormComposedProps<Values> extends Omit<FormProps<Values>, 'formId' | 'validate'>, FormInternalProps<Values> {

}

export interface FormStatics<Values> {
  displayName: string;
  defaultProps: Partial<FormComposedProps<Values>>;
}

const FormDefaultProps: Partial<FormComposedProps<any>> = {
  debounceFormStateDelay: 10000,
  autoComplete: false,
  scrollToFirstError: false,
  cleanValues: false,
};

class Form<Values extends object> extends React.PureComponent<FormComposedProps<Values>> {
  commits: (() => void)[];
  mergeBlacklist: object[];
  formScope: string[];
  formValuePath: string;
  formStatePath: string;
  pendingValuesChanges: DeepPartial<Values>;
  valuesDst: string;
  pendingFormStateChanges: DeepPartial<FormState>;
  formRef: RefObject<HTMLFormElement>;
  WrapperElement: FC<HTMLAttributes<HTMLFormElement | HTMLDivElement>>;
  queueCommitFormState: DebouncedFunc<() => void> | (() => void);
  unmounted: Boolean = false;
  unregisterVirtuals: () => void;

  constructor(props: FormComposedProps<Values>) {
    super(props);

    this.commits = [];
    this.mergeBlacklist = [];

    const {
      formId, name, ancestorFormId, parentFormScope, registerForm, dispatchSubmitFormById, validateForm, touchForm, resetForm,
      setIsSubmitting, setIsSubmitted, setIsInitialized, debounceFormStateDelay, formReducerKey, isolatedSubForm,
      initialValues, scrollToFirstError, isolate, valuesSrc, originalValuesDst, as, ancestorFormReducerKey, schema, nestedControlInfos, nestedFormInfos,
    } = props;

    if (ancestorFormId && !isolate && valuesSrc) {
      throwError(`Form: only the ancestor Form or a form with the "isolate" prop can have a "valuesSrc". Was passed valuesSrc="${valuesSrc}"`);
    }

    if (ancestorFormId && !isolate && originalValuesDst) {
      throwError(`Form: only the ancestor Form or a form with the "isolate" prop can have a "valuesDst". Was passed valuesDst="${originalValuesDst}"`);
    }

    this.formScope = ancestorFormId ? [...parentFormScope, name] : [name];

    // eslint-disable-next-line no-console
    const isStrictModeCheck = console.log.name === 'disabledLog';
    if (!isStrictModeCheck) {
      if (registerForm) {
        registerForm({
          formId,
          name,
          nestedControlInfos,
          nestedFormInfos,
          validate: validateForm,
          touch: touchForm,
          reset: (newFormState, options) => resetForm(initialValues, newFormState, options),
          setIsSubmitting,
          setIsSubmitted,
          setIsInitialized,
        });
      }
    }

    this.formValuePath = this.formScope.slice(1).join('.');
    this.formStatePath = ancestorFormId ?
      formReducerKey.replace(new RegExp(`^${ancestorFormReducerKey}.`), '') :
      formReducerKey;
    this.pendingValuesChanges = this.getInitialPendingValueChanges();

    this.valuesDst = schema.getValuesDst(this.props);

    this.pendingFormStateChanges = {};
    this.queueCommitFormState = debounce(this.commitFormState, debounceFormStateDelay);

    this.formRef = React.createRef();
    this.WrapperElement = (ancestorFormId || isolatedSubForm || as) ?
      (wrapperProps) => (
        createElement(
          as || 'div',
          {
            'data-testid': 'Form',
            'data-test-name': name,
            ...wrapperProps,
          },
        )
      ) :
      (wrapperProps) => {
        const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          dispatchSubmitFormById(formId);

          if (scrollToFirstError) {
            setTimeout(() => this.scrollFirstErrorIntoView(), 200);
          }
        }, []);

        return (
          <form
            name={name}
            autoComplete="off"
            noValidate
            data-testid="Form"
            data-test-name={name}
            ref={this.formRef}
            {...wrapperProps}
            onSubmit={handleSubmit}
          />
        );
      };

    if (!isStrictModeCheck) {
      if (ancestorFormId) {
        this.initializeState();
      } else {
        this.scheduleCommitFormState({
          isInitialized: true,
        });
      }
    }
  }

  componentDidMount() {
    const { initialValues, defaultValues, ancestorFormId } = this.props;

    if (this.checkIsAncestorInitializing()) {
      const defaultedValues = merge(defaultValues, initialValues);
      this.scheduleCommitFormValues(defaultedValues);
    } else if (defaultValues) {
      const formValues = this.getFormValues();
      const defaultedValues = merge(defaultValues, formValues);
      this.scheduleCommitFormValues(defaultedValues);
    }

    // After initialization, commits are immediate
    (this.queueCommitFormState as DebouncedFunc<() => void>).flush();

    // Avoids multiple simultaneous updates, like when multiple controls clear at once.
    if (!ancestorFormId) {
      this.queueCommitFormState = debounce(this.commitFormState, 0);
    } else {
      this.queueCommitFormState = this.commitFormState;
    }
  }

  componentWillUnmount() {
    const { formId, name, unregisterForm } = this.props;

    (this.queueCommitFormState as DebouncedFunc<() => void>).flush?.();

    if (unregisterForm) {
      unregisterForm({ formId, name });
    }

    this.unmounted = true;
    this.unregisterVirtuals?.();
  }

  initializeState() {
    if (this.unmounted) return;
    const { name, formId, store, scope } = this.props;
    const rootScope = this.formScope[0];

    this.unregisterVirtuals?.();

    const [virtuals, unregisterVirtuals] = registerVirtuals({
      actions: formActions({ formId }),
      selectors: formSelectors,
      store,
      path: `${rootScope}.${this.formStatePath}`,
      scope,
    });

    const { isSubmitted } = this.getParentFormState() || { isSubmitted: false };
    this.scheduleCommitFormState({
      formId,
      name,
      errors: {},
      warnings: {},
      cautions: {},
      touched: {},
      dirty: {},
      isInitialized: true,
      isSubmitting: false,
      isSubmitted,
      isValidating: false,
      valuesDst: this.valuesDst,
      ...virtuals,
    });

    const unsubscribe = store.subscribe(() => {
      const formState = this.getFormState();
      if (!formState || !('isDeeplyValid' in formState)) {
        this.initializeState();
      }
    });

    this.unregisterVirtuals = () => {
      unregisterVirtuals();
      unsubscribe();
    };
  }

  getFormState() {
    const { store, scope, formReducerKey } = this.props;
    const state = store.getState();
    return getScoped(state, formReducerKey, scope);
  }

  getParentFormState() {
    const { store, scope, parentFormReducerKey } = this.props;
    const state = store.getState();
    return getScoped(state, parentFormReducerKey, scope);
  }

  getAncestorFormState() {
    const { store, scope } = this.props;
    const state = store.getState();
    const rootScope = this.formScope[0];
    return getScoped(state, `${rootScope}`, scope) as FormState;
  }

  getInitialPendingValueChanges() {
    const { initialValues, defaultValues } = this.props;
    return Array.isArray(initialValues || defaultValues) ? [] : {};
  }

  getFormValueDst() {
    const { name, parentFormScope, ancestorValuesDst } = this.props;
    if (ancestorValuesDst) {
      const valuePath = parentFormScope.slice(1);
      return `${[ancestorValuesDst, ...valuePath, name].join('.').replace(/\.\[/g, '[')}`;
    }
    return this.valuesDst;
  }

  getFormValues() {
    const { store, scope } = this.props;
    const state = store.getState();
    const valuesDst = this.getFormValueDst();
    const formValues: DeepPartial<Values> = getScoped(state, valuesDst, scope);
    return formValues;
  }

  getFormValue(path: string, defaultValue?: DeepPartial<Values>): object {
    const formValues = this.getFormValues();
    return get(formValues, path, defaultValue);
  }

  getNestedFormValues(values: DeepPartial<Values>, formValuePath: string): object {
    const nestedFormValues = get(this.pendingValuesChanges, formValuePath);

    if (typeof nestedFormValues !== 'undefined') {
      return cloneDeep(nestedFormValues);
    }

    const defaultValue = Array.isArray(values) ? cloneDeep(this.getFormValue(formValuePath)) || [] : {};
    return defaultValue;
  }

  registerControl = (controlInfo: ControlInfo) => {
    const { nestedControlInfos } = this.props;
    const { controlId } = controlInfo;

    const index = nestedControlInfos.findIndex(({ controlId: currentControlId }) => (
      currentControlId === controlId
    ));

    if (index !== -1) {
      nestedControlInfos.splice(index, 1, controlInfo);
    } else {
      nestedControlInfos.push(controlInfo);
    }

    this.updateRegistrations();
  };

  unregisterControl = (controlInfo: ControlInfo) => {
    if (!this.unmounted) {
      const { controlId, name } = controlInfo;

      const { nestedControlInfos } = this.props;

      const index = nestedControlInfos.findIndex(({ controlId: currentControlId }) => (
        currentControlId === controlId
      ));
      nestedControlInfos.splice(index, 1);

      // Avoid resetting the state if another control with the same name has replaced the current control
      if (!nestedControlInfos.some((nestedControlInfo) => nestedControlInfo.name === name)) {
        const stateChanges = {};

        setInPlace(stateChanges, `errors.${name}`, undefined);
        setInPlace(stateChanges, `warnings.${name}`, undefined);
        setInPlace(stateChanges, `cautions.${name}`, undefined);
        setInPlace(stateChanges, `dirty.${name}`, undefined);
        setInPlace(stateChanges, `touched.${name}`, undefined);

        this.scheduleCommitFormState(stateChanges);
      }
    }

    this.updateRegistrations();
  };

  updateRegistrations = debounce(() => {
    const { registrations, nestedFormInfos, nestedControlInfos } = this.props;

    registrations.forEach((registration) => {
      registration({ nestedFormInfos, nestedControlInfos });
    });
  });

  registerForm = (nestedFormInfo: NestedFormInfo<object>) => {
    const { nestedFormInfos } = this.props;
    nestedFormInfos.push(nestedFormInfo as unknown as NestedFormInfo<Values>);

    this.updateRegistrations();
  };

  unregisterForm = (nestedFormInfo: Partial<NestedFormInfo<object>>) => {
    const { formId, name } = nestedFormInfo;
    const { nestedFormInfos } = this.props;
    const index = nestedFormInfos.findIndex(({ formId: currentNestedFormId }) => currentNestedFormId === formId);
    nestedFormInfos.splice(index, 1);

    const stateChanges = {};

    setInPlace(stateChanges, `forms.${name}`, undefined);

    this.scheduleCommitFormState(stateChanges);

    this.updateRegistrations();
  };

  scheduleCommitFormValues = (values: DeepPartial<Values>, { formValuePath, path, shouldMerge = true, mergeBlacklist = [], commits = [] }: {
    formValuePath?: string; path?: string; shouldMerge?: boolean; mergeBlacklist?: object[]; commits?: (() => void)[];
  } = {}) => {
    this.commits.push(...commits);
    this.mergeBlacklist.push(...mergeBlacklist);

    const isNestedForm = Boolean(formValuePath);

    if (isNestedForm) {
      const scopedPendingValues = this.getNestedFormValues(values, formValuePath);

      const updatedScopedPendingValues = merge(scopedPendingValues, values);

      setInPlace(this.pendingValuesChanges, formValuePath, updatedScopedPendingValues);
    } else if (shouldMerge) {
      mergeInPlace(this.pendingValuesChanges, values);
    } else {
      setInPlace(this.pendingValuesChanges, path, get(values, path));
    }

    const hasPendingValuesChanges = Object.keys(this.pendingValuesChanges).length;

    if (hasPendingValuesChanges) {
      this.queueCommitFormState();
    }
  };

  scheduleCommitFormState = (formStateChanges: DeepPartial<FormState>, { formStatePath }: { formStatePath?: string } = {}) => {
    const scopedPendingFormStateChanges: object = (formStatePath)
      ? get(this.pendingFormStateChanges, formStatePath, {})
      : this.pendingFormStateChanges;

    mergeState(scopedPendingFormStateChanges, formStateChanges, { clone: false });

    if (this.pendingFormStateChanges !== scopedPendingFormStateChanges) {
      setInPlace(this.pendingFormStateChanges, formStatePath, scopedPendingFormStateChanges);
    }

    this.queueCommitFormState();
  };

  setControlValue = (name: string, value: unknown, callback: () => void) => {
    if (!this.unmounted) {
      if (typeof value === 'object' && value !== null) {
        this.mergeBlacklist.push(value);
      }

      if (callback) {
        this.commits.push(callback);
      }

      const valueChanges = set({}, name, value) as DeepPartial<Values>;

      this.scheduleCommitFormValues(valueChanges, { path: name, shouldMerge: false });
    }
  };

  setControlState = (name: string, state: ControlState) => {
    const stateChanges = {};

    if (typeof state.errors !== 'undefined') {
      setInPlace(stateChanges, `errors.${name}`, state.errors);
      this.mergeBlacklist.push(state.errors);
    }

    if (typeof state.warnings !== 'undefined') {
      setInPlace(stateChanges, `warnings.${name}`, state.warnings);
      this.mergeBlacklist.push(state.warnings);
    }

    if (typeof state.cautions !== 'undefined') {
      setInPlace(stateChanges, `cautions.${name}`, state.cautions);
      this.mergeBlacklist.push(state.cautions);
    }

    if (typeof state.touched !== 'undefined') {
      setInPlace(stateChanges, `touched.${name}`, state.touched);
    }

    if (typeof state.dirty !== 'undefined') {
      setInPlace(stateChanges, `dirty.${name}`, state.dirty);
    }

    this.scheduleCommitFormState(stateChanges);
  };

  checkIsAncestorInitializing() {
    const ancestorState = this.getAncestorFormState();
    return !ancestorState || !ancestorState.isInitialized;
  }

  formatValuesToState(newValues: unknown) {
    const oldValues = this.getFormValues();
    const { valuesToStateFormatters } = this.props;

    if (!valuesToStateFormatters) {
      return newValues as Values;
    }

    return valuesToStateFormatters.reduce((currentValues: unknown, formatter) => (
      formatter(currentValues, oldValues)
    ), newValues) as Values;
  }

  commitFormState = () => {
    const { ancestorFormId, formId, dispatchUpdateFormById, ancestorCommitFormState, ancestorCommitFormValues } = this.props;

    const { pendingFormStateChanges, pendingValuesChanges, mergeBlacklist, commits } = this;

    this.pendingFormStateChanges = {};
    this.pendingValuesChanges = this.getInitialPendingValueChanges();
    this.mergeBlacklist = [];
    this.commits = [];

    if (!ancestorFormId) {
      const formattedChanges = this.formatValuesToState(pendingValuesChanges);

      commits.forEach((commit) => {
        try {
          commit();
        } catch (error) {
          coreLogger.warn('Form: Commit could not be completed. This is usually safe to ignore and means the component receiving the commit was unmounted.', error);
        }
      });

      dispatchUpdateFormById(formId, pendingFormStateChanges, formattedChanges, {
        mergeBlacklist,
      });
    }

    if (ancestorCommitFormState) {
      ancestorCommitFormState(pendingFormStateChanges, {
        formStatePath: this.formStatePath,
      });
    }

    if (ancestorCommitFormValues) {
      ancestorCommitFormValues(pendingValuesChanges, {
        formValuePath: this.formValuePath,
        mergeBlacklist,
        commits,
      });
    }
  };

  getScrollingTarget(): HTMLElement {
    const { hoistErrors } = this.props;

    const formMessage: HTMLElement = this.formRef?.current?.querySelector('[data-form-message]');
    if (formMessage && hoistErrors) return formMessage;

    const firstInvalidControl: HTMLElement = this.formRef?.current?.querySelector('[aria-errormessage][aria-invalid="true"]');

    if (firstInvalidControl) {
      const label: HTMLElement = firstInvalidControl?.closest('[data-testid="FormColLabel"]');
      return label || firstInvalidControl;
    }

    return null;
  }

  scrollFirstErrorIntoView() {
    const target = this.getScrollingTarget();

    if (target) {
      scrollIntoView(target);
    }
  }

  getFormContext = memoize(({
    ancestorFormId, formId, scope, initialValues, defaultValues, ancestorValuesDst, ancestorCommitFormValues,
    ancestorCommitFormState, formReducerKey, isolatedSubForm, disabled, readOnly, textOnly, ancestorFormReducerScope,
    ancestorFormReducerKey, hideErrorMessages, allowInvalid, nestedControlInfos, nestedFormInfos, watchRegistrations, hideHoistedAnchor, hoistErrors,
  }) => ({
    nestedControlInfos,
    nestedFormInfos,
    ancestorFormId: ancestorFormId || formId,
    ancestorFormReducerKey: ancestorFormReducerKey || formReducerKey,
    ancestorFormReducerScope: ancestorFormReducerScope || scope,
    ancestorValuesDst: ancestorValuesDst || this.valuesDst,
    ancestorCommitFormValues: ancestorCommitFormValues || this.scheduleCommitFormValues,
    ancestorCommitFormState: ancestorCommitFormState || this.scheduleCommitFormState,
    parentFormId: formId,
    parentDefaultValues: defaultValues,
    formScope: this.formScope,
    formReducerScope: scope,
    initialValues,
    valuesDst: this.getFormValueDst(),
    formReducerKey,
    isolatedSubForm,
    disabled,
    readOnly,
    textOnly,
    hideErrorMessages: hideErrorMessages || Boolean(hoistErrors),
    allowInvalid,
    hideHoistedAnchor,
    registerControl: this.registerControl,
    unregisterControl: this.unregisterControl,
    registerForm: this.registerForm,
    unregisterForm: this.unregisterForm,
    setControlValue: this.setControlValue,
    setControlState: this.setControlState,
    watchRegistrations,
  }));

  getFormColContext = memoize(({ defaultCol, parentDefaultCol, minColWidth, parentMinColWidth, formColContext }) => ({
    defaultCol: defaultCol || parentDefaultCol,
    minColWidth: minColWidth || parentMinColWidth,
    ...formColContext,
  }));

  render() {
    const {
      formId, name, children, initialValues, ancestorFormId, ancestorValuesDst, ancestorCommitFormValues,
      ancestorCommitFormState, defaultCol, minColWidth, formReducerKey, defaultValues, isolatedSubForm, disabled, readOnly, textOnly, scope, ancestorFormReducerScope,
      ancestorFormReducerKey, hoistErrors, errorMessageTitle, warningMessageTitle, cautionMessageTitle, hideErrorMessages, allowInvalid, nestedControlInfos, nestedFormInfos, hideHoistedAnchor, watchRegistrations,
    } = this.props;

    const { WrapperElement } = this;

    const newFormContext = this.getFormContext({
      ancestorFormId,
      formId,
      scope,
      initialValues,
      defaultValues,
      ancestorValuesDst,
      ancestorFormReducerKey,
      nestedControlInfos,
      nestedFormInfos,
      ancestorFormReducerScope,
      formReducerKey,
      isolatedSubForm,
      disabled,
      readOnly,
      textOnly,
      hideErrorMessages,
      allowInvalid,
      hideHoistedAnchor,
      ancestorCommitFormValues,
      ancestorCommitFormState,
      watchRegistrations,
    });

    const resolvedChildren = (typeof children === 'function') ? children({ formId, name, valuesDst: this.getFormValueDst() }) : children;

    const content = hoistErrors ?
      (
        <ControlGrouper>
          {({ controls, errors, warnings, cautions, messages, validators }) => (
            <>
              <FormMessages
                formName={name}
                errorTitle={errorMessageTitle}
                warningTitle={warningMessageTitle}
                cautionTitle={cautionMessageTitle}
                controls={controls}
                errors={errors}
                warnings={warnings}
                cautions={cautions}
                messages={messages}
                validators={validators}
              />
              {resolvedChildren}
            </>
          )}
        </ControlGrouper>
      ) :
      resolvedChildren;

    return (
      <WrapperElement {...filterKnownProps(this.props, Form.knownProps) as React.HTMLAttributes<HTMLFormElement | HTMLDivElement>}>
        <FormContext.Provider
          value={newFormContext}
        >
          <FormColContext.Consumer>
            {({
              defaultCol: parentDefaultCol = FORM.DEFAULT_COL,
              minColWidth: parentMinColWidth = FORM.DEFAULT_COL_WIDTH,
              ...formColContext
            }) => (
              <FormColContext.Provider
                value={this.getFormColContext({
                  defaultCol, parentDefaultCol, minColWidth, parentMinColWidth, formColContext,
                })}
              >
                {content}
              </FormColContext.Provider>
            )}
          </FormColContext.Consumer>
        </FormContext.Provider>
      </WrapperElement>
    );
  }

  static displayName = 'Form';
  static defaultProps = FormDefaultProps;
  static knownProps = ['children', 'name', 'valuesSrc', 'valuesDst', 'initialValues', 'onSubmit', 'defaultCol', 'minColWidth', 'formId', 'dispatchSubmitFormById', 'scope', 'store', 'validators', 'ancestorFormId', 'parentFormId', 'parentFormScope', 'parentFormScope', 'formReducerKey', 'registerForm', 'unregisterForm', 'validateForm', 'touchForm', 'resetForm', 'setIsSubmitting', 'setIsInitialized', 'setIsSubmitted', 'nestedFormInfos', 'nestedControlInfos', 'ancestorValuesDst', 'ancestorCommitFormValues', 'ancestorCommitFormState', 'defaultValues', 'dispatchUpdateFormById', 'debounceFormStateDelay', 'validate', 'valuesToStateFormatters', 'autoComplete', 'parentFormReducerKey', 'isolatedSubForm', 'isolate', 'textOnly', 'scrollToFirstError', 'cleanValues', 'hoistErrors', 'errorMessageTitle', 'originalValuesDst', 'ancestorFormReducerScope', 'ancestorFormReducerKey', 'schema', 'hideErrorMessages', 'allowInvalid', 'latestInitialValues', 'warningMessageTitle', 'cautionMessageTitle', 'watchRegistrations', 'registrations', 'hideHoistedAnchor'] as (keyof FormProps<any>)[];
  static publicProps = ['name', 'valuesDst', 'valuesSrc', 'initialValues', 'valuesToStateFormatters', 'autoComplete', 'scrollToFirstError', 'validators', 'isolate', 'defaultCol', 'onSubmit', 'minColWidth', 'defaultValues', 'validate', 'textOnly', 'disabled', 'cleanValues', 'hideErrorMessages', 'allowInvalid'] as (keyof FormProps<any>)[];
  static scopeProps = ['formId', 'name', 'errors', 'warnings', 'touched', 'dirty', 'isValid', 'isValidating', 'isSubmitting'] as (keyof FormProps<any>)[];
}

export default Form;
